import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, AlertTriangle, X as XIcon } from "lucide-react";
import { cn } from "@/lib/utils.js";

type ToastType = "success" | "error" | "warning";

interface ToastMessage { id: number; text: string; type: ToastType; action?: { label: string; onClick: () => void }; }
interface ToastContextValue { show: (text: string, type?: ToastType, action?: { label: string; onClick: () => void }) => void; }

const ToastContext = React.createContext<ToastContextValue>({ show: () => {} });

export const useToast = () => React.useContext(ToastContext);

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <Check className="w-4 h-4" />,
  error: <XIcon className="w-4 h-4" />,
  warning: <AlertTriangle className="w-4 h-4" />,
};

const STYLES: Record<ToastType, string> = {
  success: "bg-success-wash text-success-text border-success/30",
  error: "bg-error-wash text-error-text border-error/30",
  warning: "bg-warning-wash text-warning-text border-warning/30",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);
  const idRef = React.useRef(0);

  const show = React.useCallback((text: string, type: ToastType = "success", action?: { label: string; onClick: () => void }) => {
    const id = ++idRef.current;
    setMessages((prev) => [...prev, { id, text, type, action }]);
    setTimeout(() => setMessages((prev) => prev.filter((m) => m.id !== id)), action ? 6000 : 3000);
  }, []);

  const dismiss = (id: number) => setMessages((prev) => prev.filter((m) => m.id !== id));

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2" role="status" aria-live="polite">
        <AnimatePresence>
          {messages.map((m) => (
            <motion.div key={m.id} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }} className={cn("flex items-center gap-2 px-4 py-3 rounded-md border shadow-lg text-sm font-medium", STYLES[m.type])}>
              {ICONS[m.type]}{m.text}
              {m.action && (
                <button onClick={() => { m.action!.onClick(); dismiss(m.id); }} className="ml-2 underline font-semibold hover:opacity-80">{m.action.label}</button>
              )}
              <button onClick={() => dismiss(m.id)} aria-label="Dismiss notification" className="ml-2 p-0.5 rounded hover:opacity-60"><XIcon className="w-3.5 h-3.5" /></button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
